import { FormGroup } from '@angular/forms';
import { ValidationError, ValidationErrorFromAPI } from '~shared/models';
import orderBy from 'lodash.orderby';
import { FIELD_NEGATE_PATTERN } from '~shared/validators/pattern-validators';
import {
  MAX_LENGTH,
  MIN_LENGTH,
  MIN_AGE,
  SECURITY,
  DATE_PATTERN,
  EMPTY,
  UNRECOGNIZED,
  REGEXP
} from '../constants/validation-message.constant';
import { CSSHttpError } from '~shared/modules/error-handler/models/css-error.model';

export namespace ErrorsUtil {
  export function shouldShowError(
    form: FormGroup, controlName: string, targetErrorName: string, priorityErrorNameList: Array<string> = []
  ): boolean {
    const { errors } = form.get(controlName);

    const errorNameList = Object.keys(errors || {});
    const hasPriorityError = errorNameList.some(errorName => priorityErrorNameList.includes(errorName));

    return !hasPriorityError && hasFieldError(form, controlName, targetErrorName);
  }

  function hasFieldError(form: FormGroup, controlName?: string, errorName?: string): boolean {
    if (controlName) {
      const control = form.get(controlName);
      return control.invalid && Boolean(control.errors[errorName]);
    }

    return false;
  }

  function getUnallowedCharactersMessage(value: string, negatePattern: RegExp | string): string {
    const result = String(value).match(negatePattern);
    return result !== null && result.length > 0 ? 'Not allowed characters' : '';
  }

  /**
   * This method used to get human readable error and details of the field error
   * @param value the input value
   * @param errorType The error type pattern|required|maxlength
   * @param negatePattern If given a regular Exp it will find the wrong symbole otherwise will get the length
   * @returns {message} A human readable detailed message
   */
  export function getValidationErrorDetails(
    value: string,
    errorType: string,
    negatePattern: RegExp | string
  ): string {
    switch (errorType) {
      case 'pattern':
        if (negatePattern === 'length') {
          return MIN_LENGTH;
        } else {
          return getUnallowedCharactersMessage(value, negatePattern);
        }
      case 'required':
      case 'mandatory':
        return EMPTY;
      case 'germanPostalCode':
        const unallowedCharacters = getUnallowedCharactersMessage(value, negatePattern);
        const errors = [];
        const germanPostalLength = 5;
        if (unallowedCharacters.length > 0) {
          errors.push(unallowedCharacters);
        }
        if (value.length > germanPostalLength) {
          errors.push(MAX_LENGTH);
        } else if (value.length < germanPostalLength) {
          errors.push(MIN_LENGTH);
        }
        return errors.length > 0 ? errors.join(' + ') : UNRECOGNIZED;
      case 'maxlength':
        return MAX_LENGTH;
      case 'max-length':
        return MAX_LENGTH;
      case 'datePattern':
        return DATE_PATTERN;
      case 'minAge':
        return MIN_AGE;
      case 'regexp':
        return REGEXP;
      case 'security':
        return SECURITY;
      default:
        return UNRECOGNIZED;
    }
  }

  export function concatErrorsToString(errors: ValidationError[], prefix = ''): string {
    const errorString = orderBy(errors, 'inputField')
      .map(({ inputField, label }) => `${inputField || ''} - ${label || ''}`)
      .join(', ');

    return `{ ${prefix}${errorString}}`;
  }

  export function collectFormErrors(form: FormGroup): ValidationError[] {
    const formControls = Object.entries(form.controls);
    const invalidFormControls = formControls.filter((control) => control[1].invalid);
    return invalidFormControls.map((invalidControl) => {
      const errorType = Object.keys(invalidControl[1].errors)[0];
      const fieldName = invalidControl[0];
      return {
        inputField: fieldName,
        error: errorType,
        label: ErrorsUtil.getValidationErrorDetails(
          invalidControl[1].value,
          errorType,
          FIELD_NEGATE_PATTERN[fieldName]
        )
      };
    });
  }

  function getVatNumberError(inputField, error): ValidationError {
    let label: string;
    switch (error) {
      case 'invalid':
        label = 'Invalid Business Details';
        break;
      case 'notFound':
        label = 'VAT Number Not Found';
        break;
      case 'failed':
        label = 'Failed Business';
        break;
      case 'blocked':
        label = 'Split Payment business';
        break;
      default:
        label = UNRECOGNIZED;
    }

    return { inputField, error, label };
  }

  export function collectFormAPIErrors(errors): ValidationError[] {
    const formControls = Object.entries(errors.invalidParams);

    return formControls.map((invalidFormControl: [string, ValidationErrorFromAPI]) => {
      const errorType = invalidFormControl[1]?.type;
      const fieldName = invalidFormControl[0];

      if (fieldName === 'vatNumber') {
        return getVatNumberError(fieldName, errorType);
      }
      return {
        inputField: fieldName,
        error: errorType,
        label: `${ErrorsUtil.getValidationErrorDetails(
          '',
          errorType,
          FIELD_NEGATE_PATTERN[fieldName]
        )}_API`
      };
    });
  }

  export function collectVATNumberErrors(errorType): ValidationError[] {
    const inputField = 'vatNumber';
    const errorData = getVatNumberError(inputField, errorType);
    return [errorData];
  }
}
